/**
 * The sign-in gate — client review R1 (2026-08-22), rebuilt to the client's mock by **C5**.
 *
 * The geoportal's **first** page: a session goes gate → §F4 splash → app. It is an overlay
 * on the same pattern as the splash (a sibling of `<AppRoutes/>` in `App.tsx`, never a
 * route) for the same reason — the app behind it must stay mounted, so that dismissing the
 * gate is a paint and not a boot — and it suppresses itself for the session under its own
 * `sessionStorage` key (`GATE_SEEN_KEY`, `lib/state.ts`).
 *
 * ## C5: a page, not a card
 *
 * R1 shipped a 940 px centred card. The client's mock is a full-bleed two-panel page, and the
 * difference is not decoration: the left panel is where the programme introduces itself and the
 * right half is the white form: logo, *Welcome*, email, password with a reveal toggle, the two
 * inert conveniences (Remember me, Forgot password?), and the button.
 *
 * ## 2026-10-06: one frosted sheet over the field
 *
 * The client's design for this round, with the reference mockup's copy kept word for word: on
 * the left the field photograph (`gateAssets.BACKDROP`) with a single frosted sheet over it — the title
 * beside the four country pills, the subtitle, the lede, one row per use (icon, title,
 * sentence), and the tagline on a green band whose top edge is a light-brown, lime and green
 * wave; on the right the form on plain white, the attribution in a bordered card at its foot.
 * All of it is in `base.css` under *the sign-in gate: a frosted sheet*.
 *
 * ## The fields are inert, and that is the specification, not a stub
 *
 * The review asks for an email and a password that are *visible* and cost nothing: "no
 * validation, no credentials required, permissions come later". So they are real, focusable,
 * typeable inputs whose values are never read, never sent and never stored — uncontrolled,
 * outside React state entirely, with `autoComplete="off"` so no browser offers to save a
 * credential this app has no account to check it against. Two rejected alternatives, both
 * worse:
 *
 * * **`disabled` fields.** They would *look* like the gate is broken or still loading, and
 *   a reader who cannot click into an email box reads it as a fault to report.
 * * **A fake check.** Any validation at all — a required marker, a "wrong password"
 *   message, a spinner — would be a claim about an authentication system that does not
 *   exist yet. §I2's real one arrives with roles; until then the honest UI is one that lets
 *   anyone in. (A line under the buttons used to say so in words; the client removed it on
 *   2026-10-06, and *Continue as guest* now carries that message on its own.)
 *
 * *Remember me* and *Forgot password?* join them: the mock has both, and both are inert for
 * exactly the same reason — there is no session to remember and no address to send a reset
 * to. They are marked `aria-disabled` rather than `disabled` so a screen reader hears why
 * instead of hearing nothing, and each carries a `title` (`gate.inertControl`) saying so.
 *
 * ## "Continue as guest" stays, and the mock omits it
 *
 * The client's mock has *Sign In* and nothing else. Taken literally that would leave the only
 * way into a portal whose data is entirely open behind a button that authenticates nobody —
 * and would make the honest action (browsing as a guest) the one with no control. So the door
 * survives. Logged as a deviation in docs/NOTES.md.
 *
 * C5 demoted it to a discreet underlined line, and the 2026-09-02 review put it back to a
 * full-width button: until §I2 ships there is no account to sign in with, so the line of
 * 12 px text was the control that actually worked and the solid blue button was the one that
 * did nothing. It stays visually second — soft green fill, outlined, against Sign In's solid
 * blue — which is the mock's hierarchy without a 12 px tap target at the bottom of a phone.
 *
 * Both exits therefore still do exactly one thing, and the same thing: `setGateOpen(false)`.
 *
 * **ESC does not close it.** Deliberate, and the one place this overlay departs from the
 * splash's keyboard contract: the splash is a thing you dismiss, the gate is a thing you
 * pass through, and there is no hidden shortcut past it. Every exit is a labelled control.
 *
 * **It is a real modal, not just a `role` (R6 fix).** `aria-modal="true"` is a claim about
 * the whole document — it tells assistive tech to ignore everything outside this node — and a
 * dialog that makes that claim without moving focus into itself leaves a screen-reader user's
 * first screen of the session showing one dialog with their cursor nowhere in it. So on open:
 * focus lands on the email field, the rest of the document is marked `inert` (which also
 * takes the covered topbar and map controls out of the Tab order a sighted keyboard user was
 * walking through), and Tab cycles inside the page. On close, focus goes back where it came
 * from. §F2's `#toast` is deliberately left out of the `inert` sweep: it is the app's one
 * `aria-live` region, and a boot-time message (`PermalinkSync`'s "some shared settings were
 * not recognised") must still be announced to a reader who is standing at the gate.
 */
import {
  Fragment,
  useCallback,
  useEffect,
  useRef,
  useState,
  type FormEvent,
  type KeyboardEvent,
} from 'react';
import { useTranslation } from 'react-i18next';

import { Icon, type IconName } from '../../lib/icons';
import { asLanguage, DEFAULT_LANGUAGE, otherLanguage, setLanguage } from '../../lib/i18n';
import { COUNTRY_COLORS, COUNTRY_ORDER } from '../../lib/palette';
import { useAppStore } from '../../lib/state';
import { BACKDROP, LOGO, NETHERLANDS, NETHERLANDS_HREF, PARTNERS } from './gateAssets';

/**
 * The three things the client's reference mockup says the Geoportal is for, in its order: the
 * sentence, the row's short title (its own locale key, so neither language's title drifts when
 * the sentence is edited), the glyph, and the tone (`.gate-use-*` in `base.css`) — soil brown
 * for *Explore*, leaf green for *Compare*, water blue for *Track*, as the mockup colours them.
 */
const USES: { key: string; title: string; icon: IconName; tone: 'soil' | 'leaf' | 'water' }[] = [
  { key: 'gate.use1', title: 'gate.use1Title', icon: 'pin', tone: 'soil' },
  { key: 'gate.use2', title: 'gate.use2Title', icon: 'layers', tone: 'leaf' },
  { key: 'gate.use3', title: 'gate.use3Title', icon: 'chart', tone: 'water' },
];

/** Niger's palette amber is too light under white type; the band's brown stands in. */
const NIGER_PILL_BG = '#7A4A21';

/**
 * The tagline band's top edge: light brown, lime and green laid as three waves — soil, growth
 * and the band itself. `preserveAspectRatio="none"` so it spans any sheet width.
 */
function Wave() {
  return (
    <svg className="gate-wave" viewBox="0 0 800 70" preserveAspectRatio="none" aria-hidden="true" focusable="false">
      <path d="M0 40C140 6 300 -2 470 18S720 50 800 22V70H0Z" fill="#C9A27A" />
      <path d="M0 50C160 18 320 12 480 30S730 56 800 34V70H0Z" fill="#8CC63F" />
      <path d="M0 60C170 32 340 26 500 40S740 64 800 46V70H0Z" fill="#1C5A30" />
    </svg>
  );
}

/**
 * The programme's own name — the one token both approved titles share, in the same spelling.
 *
 * EN is *"Soil Values Geoportal"* and FR is *"Géoportail Soil Values"*: the word order is
 * reversed, the generic noun is translated, and the name is not. So the accent below is found
 * by looking for this substring rather than by counting words from either end, which is what
 * makes the same rule work in both languages and in any language that keeps the name intact.
 */
const BRAND_NAME = 'Soil Values';

/**
 * The title, with the programme's name picked out (client, 2026-09-11: *"we can utilize their
 * theme colors"*) and, since 2026-09-15, set as two stacked lines rather than one run-on
 * heading (client: *"the text on the left is too basic"*, working from a supplied mockup).
 *
 * It still splits **one string into three text nodes and adds no characters**: `slice(0, at)`,
 * the name, `slice(at + name.length)`. `textContent` is byte-identical, so the copy lock in
 * `overview.spec.ts` and every `toHaveTextContent` assertion read exactly what they read
 * before. The one thing it does defeat is testing-library's `getByText`, which matches an
 * element whose *direct* text-node children form the string — that assertion is rewritten to
 * `getByRole('heading', {level: 1})`, the form the same file already uses for this heading.
 *
 * **Which side is gold flipped.** The 2026-09-11 round put the colour on the *name*
 * (`BRAND_NAME`); the mockup puts it on the *generic noun* instead — "Geoportal" in EN,
 * "Géoportail" in FR — so `.gate-title-generic` is applied to whichever of `before`/`after` is
 * non-empty rather than to `BRAND_NAME` itself. Both halves are `display:block`, which is what
 * turns the one heading into two lines *in whichever order the locale puts them*: EN has the
 * name first and the generic noun second, FR has it the other way, and neither is hard-coded —
 * the DOM order already follows the string, this only adds a line break between the two
 * pieces. An empty half (FR's `after`, EN's `before`) renders nothing rather than an empty
 * block, so there is never a blank third line.
 *
 * A locale whose title does not contain the name gets the plain string back, uncoloured and
 * on one line, rather than a mis-sliced one.
 */
function TitleInk({ title }: { title: string }) {
  const at = title.indexOf(BRAND_NAME);
  if (at < 0) return <>{title}</>;
  const before = title.slice(0, at);
  const after = title.slice(at + BRAND_NAME.length);
  return (
    <>
      {before && <span className="gate-title-generic">{before}</span>}
      <span className="gate-title-brand">{BRAND_NAME}</span>
      {after && <span className="gate-title-generic">{after}</span>}
    </>
  );
}

/**
 * The slogan, cut into its sentences so each can take a colour of its own (client,
 * 2026-09-11: *"for the slogan can we make Explore the map different color. Understand the
 * landscape also a different color. Turn data into decisions also a different color."*).
 *
 * **It adds no characters and removes none.** Every match contributes its `ink` and its `gap`
 * back to the output in source order, the scan refuses any string it cannot walk end to end,
 * and the reassembly is asserted in both languages by unit test — so `textContent` is
 * byte-identical to `t('gate.tagline')` and the copy lock in `overview.spec.ts` reads exactly
 * what it read before.
 *
 * **Why a scan and not `split('. ')`.** French is the reason. The separator between sentences
 * is not the only thing that differs between the two approved strings: French typography puts
 * a non-breaking space *inside* a sentence before `!`, `?`, `;` and `:`, so a naive split on a
 * full stop is both wrong at the boundary and capable of cutting a sentence that contains no
 * full stop at all. Here the terminal
 * punctuation is captured *with* its sentence (`ink`) and only the run of whitespace after it
 * becomes a separator (`gap`) — which is the same rule in both languages, and the rule that
 * keeps a NBSP on the side of the text it belongs to.
 *
 * The `gap` is deliberately left **outside** the coloured span. A coloured span that ended in
 * whitespace would carry its colour into the space between two sentences, which is invisible
 * on screen and wrong the moment the line wraps there.
 */
type SloganPart = { ink: string; gap: string };

function sloganParts(text: string): SloganPart[] | null {
  const parts: SloganPart[] = [];
  let consumed = 0;
  for (const m of text.matchAll(/([^.!?…]*[.!?…]+)(\s*)/gu)) {
    // A match that does not start where the last one ended means the string has a shape this
    // rule does not describe. Give up rather than emit a re-ordered or lossy slogan.
    if (m.index !== consumed) return null;
    parts.push({ ink: m[1] ?? '', gap: m[2] ?? '' });
    consumed += m[0].length;
  }
  // Three colours need three sentences. A locale that ever translates the slogan as one
  // sentence, or leaves a tail after the last full stop, gets the plain string back.
  return consumed === text.length && parts.length >= 3 ? parts : null;
}

/**
 * The triad, and the order is the argument: a cool open, a neutral middle, the programme's
 * own gold on the payoff. The hexes live in `base.css`; this only says which phrase is which.
 */
const SLOGAN_INK = ['gate-slogan-a', 'gate-slogan-b', 'gate-slogan-c'] as const;

function SloganInk({ text }: { text: string }) {
  const parts = sloganParts(text);
  if (!parts) return <>{text}</>;
  const last = parts.length - 1;
  return (
    <>
      {parts.map((p, i) => (
        <Fragment key={i}>
          <span className={SLOGAN_INK[i === 0 ? 0 : i === last ? 2 : 1]}>{p.ink}</span>
          {p.gap}
        </Fragment>
      ))}
    </>
  );
}

/**
 * Everything inside the page a Tab can reach, in document order.
 *
 * Queried on each keypress rather than cached: the contents change under the reader (the
 * EN/FR button re-renders the whole form, the reveal toggle swaps its own icon), and a stale
 * list would trap Tab on a node that is no longer there.
 */
const TABBABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function SignInGate() {
  const { t, i18n } = useTranslation();
  const open = useAppStore((s) => s.gateOpen);
  const setGateOpen = useAppStore((s) => s.setGateOpen);
  /**
   * Read off the i18next instance, as the topbar's toggle is, so it re-renders on
   * `languageChanged` whoever caused it.
   */
  const language = asLanguage(i18n.resolvedLanguage ?? i18n.language) ?? DEFAULT_LANGUAGE;

  /** The mock's eye toggle. The one piece of state on this page, and it is about pixels. */
  const [revealed, setRevealed] = useState(false);

  const enter = useCallback(() => setGateOpen(false), [setGateOpen]);

  /**
   * A real `<form>`, so Enter in either field submits — a sign-in page where the return key
   * does nothing is a sign-in page that feels broken. `preventDefault` because there is
   * nowhere to submit to; the handler is the same `enter` both exits call.
   */
  const onSubmit = useCallback(
    (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      enter();
    },
    [enter],
  );

  const overlayRef = useRef<HTMLDivElement | null>(null);
  const emailRef = useRef<HTMLInputElement | null>(null);

  /**
   * The modal half of `aria-modal`: focus in, the document behind out, focus back on close.
   *
   * The gate is a sibling of the whole app under `#root` (`app/App.tsx`), so "the document
   * behind" is precisely this node's siblings — no wrapper element, and therefore no chance of
   * a layout change from adding one.
   */
  useEffect(() => {
    const overlay = overlayRef.current;
    if (!open || !overlay) return;

    const behind = Array.from(overlay.parentElement?.children ?? []).filter(
      (el): el is HTMLElement =>
        el instanceof HTMLElement && el !== overlay && el.getAttribute('role') !== 'status',
    );
    for (const el of behind) el.setAttribute('inert', '');

    const cameFrom = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    // Not on a touch screen or in the one-column layout (≤900px, base.css): focusing the
    // field there raises the keyboard and scrolls the art panel out of view before the
    // visitor has seen the page. jsdom has no `matchMedia`, and there the focus stands.
    const handheld =
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(pointer: coarse), (max-width: 900px)').matches;
    if (!handheld) emailRef.current?.focus();

    return () => {
      for (const el of behind) el.removeAttribute('inert');
      if (cameFrom?.isConnected) cameFrom.focus();
    };
  }, [open]);

  /**
   * Tab wraps inside the page. `inert` already keeps focus out of the app behind, but it does
   * not stop Tab leaving for the browser's own chrome and coming back somewhere arbitrary —
   * and it is the newer of the two mechanisms, so this is also the floor on a browser without.
   */
  const trapTab = useCallback((e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Tab') return;
    const stops = Array.from(overlayRef.current?.querySelectorAll<HTMLElement>(TABBABLE) ?? []);
    const first = stops[0];
    const last = stops[stops.length - 1];
    if (!first || !last) return;
    if (document.activeElement !== (e.shiftKey ? first : last)) return;
    e.preventDefault();
    (e.shiftKey ? last : first).focus();
  }, []);

  if (!open) return null;

  return (
    <div
      id="gateOv"
      ref={overlayRef}
      role="dialog"
      aria-modal="true"
      aria-labelledby="gateTitle"
      data-testid="signin-gate"
      onKeyDown={trapTab}
    >
      <div id="gateCard">
        {/* ---------------------------------------------------------------- left: the panel */}
        <section id="gateArt">
          {BACKDROP && <img className="gate-art-photo" src={BACKDROP} alt="" aria-hidden="true" />}

          <div className="gate-sheet">
            <div className="gate-sheet-body">
              <div className="gate-head">
                <h1 id="gateTitle">
                  <span className="gate-plate">
                    <TitleInk title={t('gate.title')} />
                  </span>
                </h1>
                <span className="gate-pills" aria-hidden="true">
                  {COUNTRY_ORDER.map((iso) => (
                    <span
                      key={iso}
                      title={t(`country.${iso}`)}
                      style={{ backgroundColor: iso === 'NER' ? NIGER_PILL_BG : COUNTRY_COLORS[iso] }}
                    >
                      {iso.slice(0, 2)}
                    </span>
                  ))}
                </span>
              </div>
              <p className="gate-subtitle">
                <span className="gate-plate">{t('gate.subtitle')}</span>
              </p>
              <p className="gate-lede">
                <span className="gate-plate">{t('gate.lede1')}</span>
              </p>
              <ul className="gate-uses">
                {USES.map((u) => (
                  <li key={u.key} className={`gate-use gate-use-${u.tone}`}>
                    <span className="gate-use-chip">
                      <Icon name={u.icon} size={20} />
                    </span>
                    <span className="gate-use-text">
                      <span className="gate-use-title">{t(u.title)}</span>
                      <span className="gate-plate">{t(u.key)}</span>
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="gate-tagline-band">
              <Wave />
              <p className="gate-tagline">
                <Icon name="sprout" size={22} className="gate-tagline-ico" />
                <strong>
                  <span className="gate-plate">
                    <SloganInk text={t('gate.tagline')} />
                  </span>
                </strong>
              </p>
              {/* The reference mockup's small closing wordmark — the approved title string rather
                  than a new locale key, since the two are the same words in every language. */}
              <p className="gate-wordmark">
                <span className="gate-wordmark-dash" aria-hidden="true" />
                {t('gate.title')}
              </p>
            </div>
          </div>
        </section>

        {/* ---------------------------------------------------------------- right: the form */}
        <section id="gateForm">
          {/* §F12's EN/FR toggle lives in the topbar — which is now *behind* the first page a
              reader sees. `initialLanguage()` reads a stored preference and otherwise falls
              back to English, so without this a francophone visitor's first screen would be
              English with no control on it. Same behaviour and same storage as the topbar's
              button; the label is the language you are *in*. */}
          <button
            type="button"
            id="gateLang"
            lang={language}
            title={t('topbar.switchLanguage')}
            aria-label={t('topbar.switchLanguage')}
            onClick={() => {
              void setLanguage(otherLanguage(language));
            }}
          >
            {t('topbar.lang')}
          </button>

          <div id="gateFormInner">
            <img className="gate-logo" src={LOGO} alt="" width={132} height={132} />
            <h2 className="gate-welcome">{t('gate.welcome')}</h2>
            <p className="gate-sub">{t('gate.sub')}</p>

            <form onSubmit={onSubmit} noValidate>
              <label className="fld" htmlFor="gateUser">
                {t('gate.email')}
              </label>
              <div className="gate-field">
                <Icon name="mail" size={16} className="gate-field-ico" />
                <input
                  id="gateUser"
                  ref={emailRef}
                  className="std"
                  type="email"
                  name="email"
                  autoComplete="off"
                  spellCheck={false}
                  placeholder={t('gate.emailPh')}
                />
              </div>

              <label className="fld" htmlFor="gatePass">
                {t('gate.password')}
              </label>
              <div className="gate-pass gate-field">
                <Icon name="lock" size={16} className="gate-field-ico" />
                <input
                  id="gatePass"
                  className="std"
                  type={revealed ? 'text' : 'password'}
                  name="password"
                  autoComplete="off"
                  placeholder={t('gate.passwordPh')}
                />
                <button
                  type="button"
                  id="gatePassEye"
                  className="gate-eye"
                  aria-controls="gatePass"
                  aria-pressed={revealed}
                  title={t(revealed ? 'gate.hidePassword' : 'gate.showPassword')}
                  aria-label={t(revealed ? 'gate.hidePassword' : 'gate.showPassword')}
                  onClick={() => setRevealed((v) => !v)}
                >
                  <Icon name={revealed ? 'eyeOff' : 'eye'} size={16} />
                </button>
              </div>

              <div className="gate-row">
                {/*
                  `aria-disabled`, not `disabled`: both controls are in the mock and neither
                  does anything yet, and a control the browser refuses to focus cannot tell a
                  screen-reader user *why*. They are reachable, they announce themselves as
                  unavailable, and the handlers below refuse the interaction rather than
                  pretending to have performed it.
                */}
                <label className="gate-remember" htmlFor="gateRemember">
                  <input
                    type="checkbox"
                    id="gateRemember"
                    aria-disabled="true"
                    title={t('gate.inertControl')}
                    checked={false}
                    onChange={() => undefined}
                  />
                  {t('gate.remember')}
                </label>
                <button
                  type="button"
                  id="gateForgot"
                  className="gate-forgot"
                  aria-disabled="true"
                  title={t('gate.inertControl')}
                  onClick={(e) => e.preventDefault()}
                >
                  {t('gate.forgot')}
                </button>
              </div>

              <button type="submit" className="gate-signin" id="gateSignIn">
                <Icon name="signIn" size={18} />
                {t('gate.signIn')}
              </button>
            </form>

            {/* The mock has no guest door. The portal's data is open and its honest action is
                to browse it, so the door stays — and after the 2026-09-02 review it is a
                full-width button again rather than a line of underlined text. Nobody signing
                in has an account yet, so on today's build *this* is the control that works,
                and a control that works has to be tappable on a phone. It is still second:
                soft-filled and outlined against Sign In's solid blue. */}
            <button type="button" className="gate-guest" id="gateGuest" onClick={enter}>
              <Icon name="user" size={18} />
              {t('gate.guest')}
            </button>

          </div>

          {/*
            The attribution band, arranged as IFDC's brand guide arranges it: one light card
            carrying *Funded by* with the donor mark on the left and *Coalition Partners*
            with the partner grid on the right, and a single dashed rule running the whole
            width under both headings.

            The rule is why this is one three-row grid rather than two columns side by side.
            A border under each heading would be two rules with the gutter's gap punched
            between them; here the headings occupy row 1, the rule is one element spanning
            row 2 from the first column to the last, and the artwork sits in row 3. Every
            child is placed explicitly (see base.css), so the document order below can stay
            the order a screen reader wants — each heading, then the marks it labels, tied
            together by `aria-labelledby` since the grid has separated them visually.

            Every mark is a link to its organisation (`gateAssets.ts` holds the addresses,
            each verified 2026-09-08). `rel="noopener noreferrer"` on all of them: these are
            third-party sites and none of them needs a handle on this window.

            The band is a light card for the reason the two white cards it replaced were:
            several of these logos are gradients or dark type on transparency (SNV, Landscape
            Alliance, IITA); dropped onto a dark ground they would be invisible, and inverting
            them is not ours to do to somebody's brand. Each mark additionally sits on its own
            white chip, which is what makes the "even grid" visible as a grid — the cells are
            identical, and now they look it.

            **It is at the foot of the *right* panel since 2026-09-08**, at the client's
            instruction: *"move the whole logo section to the right, below, as it appears on
            the left — do not change anything else."* So this is the same markup it was, in the
            same order, with the same ids and the same 3 + 5; only its parent changed, from
            `.gate-art-inner` to `#gateForm`.
          */}
          <div className="gate-band" data-gateband="">
            <h2 className="gate-band-title gate-band-funded" id="gateFundedTitle">
              {t('gate.fundedBy')}
            </h2>
            <h2 className="gate-band-title gate-band-coalition" id="gatePartnersTitle">
              {t('gate.partners')}
            </h2>
            <div className="gate-band-rule" data-gatebandrule="" aria-hidden="true" />

            <div
              className="gate-nl"
              data-gatenl=""
              role="group"
              aria-labelledby="gateFundedTitle"
            >
              <a
                className="gate-mark gate-mark-donor"
                data-partner="netherlands"
                data-fit="wide"
                href={NETHERLANDS_HREF}
                target="_blank"
                rel="noopener noreferrer"
              >
                {NETHERLANDS ? (
                  <img src={NETHERLANDS} alt={t('gate.netherlands')} />
                ) : (
                  <span className="gate-mark-text">{t('gate.netherlands')}</span>
                )}
              </a>
            </div>

            <div
              className="gate-partner-row"
              data-gatepartners=""
              role="group"
              aria-labelledby="gatePartnersTitle"
            >
              {/* Two real rows, not one 8-item wrap relying on a forced CSS break: the three
                  big marks (IFDC/SNV/Wageningen) and the five small ones each need their own
                  gap to land on a shared start *and* end edge, and — found the hard way —
                  letting the browser's own flex line-building decide where the wrap falls
                  doesn't reliably keep a row's own items together even when the arithmetic
                  says they should fit. Two explicit flex containers make each row's own
                  wrapping (or lack of it) exactly what its own CSS says, nothing inferred. */}
              <div className="gate-partner-line gate-partner-line-1">
                {PARTNERS.slice(0, 3).map((p) => (
                  <a
                    key={p.slug}
                    className="gate-mark gate-partner"
                    data-partner={p.slug}
                    data-fit={p.fit}
                    href={p.href}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {p.url ? (
                      <img src={p.url} alt={p.name} />
                    ) : (
                      <span className="gate-mark-text gate-partner-text">{p.name}</span>
                    )}
                  </a>
                ))}
              </div>
              <div className="gate-partner-line gate-partner-line-2">
                {PARTNERS.slice(3).map((p) => (
                  <a
                    key={p.slug}
                    className="gate-mark gate-partner"
                    data-partner={p.slug}
                    data-fit={p.fit}
                    href={p.href}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {p.url ? (
                      <img src={p.url} alt={p.name} />
                    ) : (
                      <span className="gate-mark-text gate-partner-text">{p.name}</span>
                    )}
                  </a>
                ))}
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}

export default SignInGate;
